import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Button,
  Chip,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { usePageTitle } from '../hooks/usePageTitle';
import { chatApi, ChatMessage, ChatSession } from '../services/api/chat';
import { KnightRiderLoader } from '../components/KnightRiderLoader';
import { useAuth } from '../hooks/useAuth';
import { LightbulbOutlined as ImproveIcon } from '@mui/icons-material';
import { ImproveMessageModal } from '../components/ImproveMessageModal';
import { IntermediateMessage } from '../components/chat/IntermediateMessage';

export const Chat: React.FC = () => {
  usePageTitle('Chat with Mia');
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [showSessionDrawer, setShowSessionDrawer] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingInitialSession, setIsLoadingInitialSession] = useState(true);
  const [improveModalOpen, setImproveModalOpen] = useState(false);
  const [improveMessageId, setImproveMessageId] = useState<string | null>(null);
  const [operationProgress, setOperationProgress] = useState<string | null>(null);
  const [intermediateMessages, setIntermediateMessages] = useState<Array<{
    id: string;
    text: string;
    timestamp: string;
    type: string;
    metadata?: any;
  }>>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionsLastLoadedRef = useRef<number>(0); // Track when sessions were last loaded
  const seenIntermediateMessageIds = useRef<Set<string>>(new Set()); // Track which intermediate messages we've already seen

  // Feedback state for action buttons (per message)
  const [messageFeedback, setMessageFeedback] = useState<Record<string, 'up' | 'down' | null>>({});
  const [showFeedbackForm, setShowFeedbackForm] = useState<Record<string, boolean>>({});
  const [feedbackText, setFeedbackText] = useState<Record<string, string>>({});
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  // Check if user has admin permission
  const hasAdminPermission = user?.permissions?.includes('market_compass.admin') || false;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load most recent session on mount (only if less than 30 minutes old)
  // This also pre-loads the chat history so it displays quickly when clicking the history icon
  useEffect(() => {
    const loadMostRecentSession = async () => {
      setIsLoadingInitialSession(true);
      try {
        const sessionsList = await chatApi.getSessions(10);
        setSessions(sessionsList); // Pre-load sessions for instant display in history drawer
        sessionsLastLoadedRef.current = Date.now(); // Track cache timestamp

        // If there are sessions, check if the most recent one is less than 30 minutes old
        if (sessionsList.length > 0) {
          const mostRecentSession = sessionsList[0];
          const lastMessageTime = new Date(mostRecentSession.lastMessageTime);
          const now = new Date();
          const thirtyMinutesInMs = 30 * 60 * 1000;
          const timeSinceLastMessage = now.getTime() - lastMessageTime.getTime();

          // Only load the session if it's less than 30 minutes old
          if (timeSinceLastMessage < thirtyMinutesInMs) {
            const sessionData = await chatApi.getSessionMessages(mostRecentSession.sessionId);
            setCurrentSessionId(mostRecentSession.sessionId);

            // Convert session messages to ChatMessage format
            const loadedMessages: ChatMessage[] = sessionData.messages.map((msg) => ({
              id: msg.id || `${mostRecentSession.sessionId}-temp`,
              message: msg.message,
              isUser: msg.role === 'user',
              timestamp: new Date(msg.timestamp),
            }));

            setMessages(loadedMessages);
          }
          // If session is older than 30 minutes, just show empty chat (no action needed)
        }
      } catch (error: any) {
        console.error('Error loading most recent session:', error);
        // Don't show error to user on initial load - just start with empty chat
      } finally {
        setIsLoadingInitialSession(false);
      }
    };

    loadMostRecentSession();
  }, []); // Empty dependency array - only run on mount

  const loadSessions = async () => {
    setIsLoadingSessions(true);
    try {
      const sessionsList = await chatApi.getSessions(10);
      setSessions(sessionsList);
      sessionsLastLoadedRef.current = Date.now(); // Update cache timestamp
    } catch (error: any) {
      console.error('Error loading sessions:', error);
      enqueueSnackbar('Failed to load chat sessions', { variant: 'error' });
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleNewChat = async () => {
    try {
      const newSessionId = await chatApi.createNewSession();
      setCurrentSessionId(newSessionId);
      setMessages([]);
      setShowSessionDrawer(false);
      enqueueSnackbar('New chat started', { variant: 'success' });
    } catch (error: any) {
      console.error('Error creating new session:', error);
      enqueueSnackbar('Failed to create new chat', { variant: 'error' });
    }
  };

  const handleLoadSession = async (sessionId: string) => {
    try {
      const sessionData = await chatApi.getSessionMessages(sessionId);
      setCurrentSessionId(sessionId);

      // Convert session messages to ChatMessage format
      const loadedMessages: ChatMessage[] = sessionData.messages.map((msg) => ({
        id: msg.id || `${sessionId}-temp`,
        message: msg.message,
        isUser: msg.role === 'user',
        timestamp: new Date(msg.timestamp),
      }));

      setMessages(loadedMessages);
      setShowSessionDrawer(false);
      enqueueSnackbar('Chat session loaded', { variant: 'success' });
    } catch (error: any) {
      console.error('Error loading session:', error);
      enqueueSnackbar('Failed to load chat session', { variant: 'error' });
    }
  };

  const handleOpenSessionDrawer = () => {
    setShowSessionDrawer(true);

    // Only refresh sessions if cache is stale (more than 1 minute old)
    const now = Date.now();
    const oneMinuteInMs = 60 * 1000;
    const timeSinceLastLoad = now - sessionsLastLoadedRef.current;

    if (timeSinceLastLoad > oneMinuteInMs) {
      loadSessions();
    }
  };

  const handleDeleteSession = async (sessionId: string, event: React.MouseEvent) => {
    // Stop propagation to prevent loading the session when clicking delete
    event.stopPropagation();

    if (!confirm('Are you sure you want to delete this chat session?')) {
      return;
    }

    try {
      await chatApi.deleteSession(sessionId);
      enqueueSnackbar('Chat session deleted', { variant: 'success' });

      // If the deleted session was the current one, clear the messages
      if (sessionId === currentSessionId) {
        setMessages([]);
        setCurrentSessionId(null);
      }

      // Reload sessions to update the list
      await loadSessions();
    } catch (error: any) {
      console.error('Error deleting session:', error);
      enqueueSnackbar('Failed to delete session', { variant: 'error' });
    }
  };

  const handleSendMessage = async (messageOverride?: string) => {
    const messageToSend = messageOverride || inputMessage;
    if (!messageToSend.trim() || isSending) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      message: messageToSend,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageText = messageToSend; // Capture for polling
    setInputMessage('');
    setIsSending(true);
    setOperationProgress(null); // Clear previous progress
    setIntermediateMessages([]); // Clear previous intermediate messages
    seenIntermediateMessageIds.current.clear(); // Reset tracking

    try {
      // If this is the first message in an empty chat, create a new session
      let sessionIdToUse = currentSessionId;
      if (messages.length === 0 && !currentSessionId) {
        try {
          sessionIdToUse = await chatApi.createNewSession();
          setCurrentSessionId(sessionIdToUse);
        } catch (error: any) {
          console.error('Error creating new session:', error);
          enqueueSnackbar('Failed to create new chat session', { variant: 'error' });
          setIsSending(false);
          return;
        }
      }

      // Send message and get operation ID
      const response = await chatApi.sendMessage(messageText, sessionIdToUse || undefined);

      if (!response.operationId) {
        console.error('[Chat] No operation ID in response:', response);
        enqueueSnackbar('Failed to start chat operation', { variant: 'error' });
        setIntermediateMessages([]); // Clear intermediate messages on operation start failure
        setIsSending(false);
        return;
      }

      // Poll for operation status
      const pollInterval = 2000; // Poll every 2 seconds
      const maxAttempts = 150; // Max 5 minutes (150 * 2 seconds)
      let attempts = 0;

      const poll = async (): Promise<void> => {
        attempts++;

        if (attempts > maxAttempts) {
          console.error('[Chat] Polling timeout');
          enqueueSnackbar('Request timed out. Please try again.', { variant: 'error' });
          setIntermediateMessages([]); // Clear intermediate messages on timeout
          setIsSending(false);
          return;
        }

        try {
          const status = await chatApi.getOperationStatus(response.operationId);
          console.log('[Chat] Operation status:', status.operation.status, status.operation.progress_text);

          // Update progress text
          setOperationProgress(status.operation.progress_text || 'Processing...');

          // Extract and display intermediate messages
          if (status.operation.result?.intermediateMessages) {
            const messages = status.operation.result.intermediateMessages;

            // Filter out messages we've already seen
            const newMessages = messages.filter((msg: any) => !seenIntermediateMessageIds.current.has(msg.id));

            if (newMessages.length > 0) {
              console.log('[Chat] Received new intermediate messages:', newMessages.length);

              // Track these message IDs
              newMessages.forEach((msg: any) => seenIntermediateMessageIds.current.add(msg.id));

              // Update state with new messages, filtering duplicate task lists
              setIntermediateMessages((prev) => {
                let updatedMessages = [...prev, ...newMessages];

                // Check if any new messages are task list updates
                const hasTaskListUpdate = newMessages.some(
                  (msg: any) => msg.metadata?.action === 'create' || msg.metadata?.action === 'update_status'
                );

                if (hasTaskListUpdate) {
                  // Find all task list messages
                  const taskListMessages = updatedMessages.filter(
                    (msg: any) => msg.metadata?.action === 'create' || msg.metadata?.action === 'update_status'
                  );

                  // Find the most recent task list message (last one)
                  const latestTaskList = taskListMessages[taskListMessages.length - 1];

                  // Remove all task list messages except the latest one
                  updatedMessages = updatedMessages.filter(
                    (msg: any) => {
                      const isTaskList = msg.metadata?.action === 'create' || msg.metadata?.action === 'update_status';
                      return !isTaskList || msg.id === latestTaskList.id;
                    }
                  );
                }

                return updatedMessages;
              });
            }
          }

          if (status.operation.status === 'completed') {
            // Operation completed successfully
            const result = status.operation.result;

            if (!result.message || result.message.trim() === '') {
              console.error('[Chat] Received empty message from operation');
              enqueueSnackbar('Received empty response. Please try again.', { variant: 'error' });
              setIntermediateMessages([]); // Clear intermediate messages on empty response
              setIsSending(false);
              return;
            }

            // Update session ID from result
            if (result.sessionId && result.sessionId !== currentSessionId) {
              setCurrentSessionId(result.sessionId);
            }

            const botMessage: ChatMessage = {
              id: (Date.now() + 1).toString(),
              message: result.message,
              isUser: false,
              timestamp: new Date(),
              searchResults: result.searchResults,
              tableImages: result.tableImages,
            };

            // Debug logging
            if (result.tableImages && result.tableImages.length > 0) {
              console.log('[Chat] Received table images:', result.tableImages);
            } else {
              console.log('[Chat] No table images in response');
            }

            setMessages((prev) => [...prev, botMessage]);
            setIntermediateMessages([]); // Clear intermediate messages after final response
            setIsSending(false);
          } else if (status.operation.status === 'error') {
            // Operation failed
            console.error('[Chat] Operation failed:', status.operation.error_message);
            enqueueSnackbar(
              status.operation.error_message || 'Failed to process message',
              { variant: 'error' }
            );
            setIntermediateMessages([]); // Clear intermediate messages on error
            setIsSending(false);
          } else {
            // Still processing, poll again
            setTimeout(poll, pollInterval);
          }
        } catch (error: any) {
          console.error('[Chat] Error polling operation status:', error);
          enqueueSnackbar('Error checking message status', { variant: 'error' });
          setIntermediateMessages([]); // Clear intermediate messages on polling error
          setIsSending(false);
        }
      };

      // Start polling
      setTimeout(poll, pollInterval);
    } catch (error: any) {
      console.error('Error sending message:', error);
      enqueueSnackbar(error.message || 'Failed to send message', { variant: 'error' });
      setIntermediateMessages([]); // Clear intermediate messages on send error
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleImproveMessage = (messageId: string) => {
    setImproveMessageId(messageId);
    setImproveModalOpen(true);
  };

  const handleEmailRequest = () => {
    setInputMessage('Send me an email with all the information from this conversation.');
  };

  const handleSampleMessageClick = (message: string) => {
    handleSendMessage(message);
  };

  // Action button handlers
  const handleCopyMessage = async (messageId: string, content: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  };

  const handleThumbsUp = async (messageId: string) => {
    // Validate that message has a database UUID (not a timestamp string)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(messageId)) {
      console.warn('Cannot submit feedback for message without database ID:', messageId);
      return;
    }

    const currentFeedback = messageFeedback[messageId];

    // Toggle thumbs up - if already up, delete it
    if (currentFeedback === 'up') {
      try {
        await chatApi.deleteFeedback(messageId);
        setMessageFeedback(prev => ({ ...prev, [messageId]: null }));
      } catch (error) {
        console.error('Failed to delete positive feedback:', error);
      }
      return;
    }

    setMessageFeedback(prev => ({ ...prev, [messageId]: 'up' }));
    setShowFeedbackForm(prev => ({ ...prev, [messageId]: false }));

    try {
      await chatApi.submitFeedback(messageId, 'positive');
    } catch (error) {
      console.error('Failed to submit positive feedback:', error);
      setMessageFeedback(prev => ({ ...prev, [messageId]: null }));
    }
  };

  const handleThumbsDown = async (messageId: string) => {
    // Validate that message has a database UUID (not a timestamp string)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(messageId)) {
      console.warn('Cannot submit feedback for message without database ID:', messageId);
      return;
    }

    const currentFeedback = messageFeedback[messageId];

    // Toggle thumbs down - if already down, delete it
    if (currentFeedback === 'down') {
      try {
        await chatApi.deleteFeedback(messageId);
        setMessageFeedback(prev => ({ ...prev, [messageId]: null }));
        setShowFeedbackForm(prev => ({ ...prev, [messageId]: false }));
      } catch (error) {
        console.error('Failed to delete negative feedback:', error);
      }
      return;
    }

    setMessageFeedback(prev => ({ ...prev, [messageId]: 'down' }));
    setShowFeedbackForm(prev => ({ ...prev, [messageId]: true }));
  };

  const handleSubmitFeedback = async (messageId: string) => {
    // Validate that message has a database UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(messageId)) {
      console.warn('Cannot submit feedback for message without database ID:', messageId);
      return;
    }

    const text = feedbackText[messageId];
    if (!text || !text.trim()) return;

    try {
      await chatApi.submitFeedback(messageId, 'negative', text);
      setFeedbackText(prev => ({ ...prev, [messageId]: '' }));
      setShowFeedbackForm(prev => ({ ...prev, [messageId]: false }));
      enqueueSnackbar('Feedback submitted', { variant: 'success' });
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      enqueueSnackbar('Failed to submit feedback', { variant: 'error' });
    }
  };

  // Sample messages for new chat
  const sampleMessages = [
    'What can you help me with?',
    'Show me Marriott Luxury hotels in top US markets',
    'Send me the top 20 US markets by email',
    'What tools do you have access to?',
  ];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
      }}
    >
      {/* Chat Header Section */}
      <AppBar
        position="static"
        sx={{
          flexShrink: 0,
          backgroundColor: '#F5A800',
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#FFFFFF' }}>
            Chat with Mia
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="medium"
              onClick={handleNewChat}
              sx={{
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              title="New Chat"
            >
              <AddIcon />
            </IconButton>
            <IconButton
              size="medium"
              onClick={handleOpenSessionDrawer}
              sx={{
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              title="Chat History"
            >
              <HistoryIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Scrollable Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 2,
          pt: 2,
          pb: 2,
        }}
      >
        {/* Messages */}
        {isLoadingInitialSession ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
            }}
          >
            <CircularProgress sx={{ color: '#F5A800', mb: 2 }} />
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Loading your recent conversation...
            </Typography>
          </Box>
        ) : messages.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '200px',
              gap: 2,
              px: 2,
            }}
          >
            <Typography variant="body1" color="text.secondary" textAlign="center" mb={1}>
              Try asking me...
            </Typography>
            {sampleMessages.map((message, index) => (
              <Chip
                key={index}
                label={message}
                onClick={() => handleSampleMessageClick(message)}
                sx={{
                  fontSize: '1rem',
                  height: 'auto',
                  py: 1.5,
                  px: 2,
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    padding: '8px 12px',
                    fontStyle: 'italic',
                  },
                  backgroundColor: '#F5F5F5',
                  color: '#424242',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#E0E0E0',
                  },
                  maxWidth: '90%',
                }}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: msg.isUser ? 'flex-end' : 'flex-start',
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    maxWidth: msg.isUser ? '75%' : 'calc(100% - 20px)',
                    px: 2,
                    py: 1.5,
                    backgroundColor: msg.isUser ? '#F5A800' : '#f5f5f5',
                    color: msg.isUser ? '#FFFFFF' : '#000000',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                  }}
                >
                  {msg.isUser ? (
                    <Typography
                      variant="body1"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {msg.message}
                    </Typography>
                  ) : (
                    <>
                      <Box
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7em',
                          '& p': {
                            margin: '0.5em 0',
                            '&:first-of-type': { marginTop: 0 },
                            '&:last-of-type': { marginBottom: 0 }
                          },
                          '& ul, & ol': {
                            margin: '0.5em 0',
                            paddingLeft: '1.5em'
                          },
                          '& li': {
                            margin: '0.25em 0'
                          },
                          '& h1, & h2, & h3, & h4, & h5, & h6': {
                            margin: '0.75em 0 0.5em 0',
                            fontWeight: 'bold',
                            '&:first-of-type': { marginTop: 0 }
                          },
                          '& code': {
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            padding: '0.2em 0.4em',
                            borderRadius: '3px',
                            fontFamily: 'monospace',
                            fontSize: '0.7em'
                          },
                          '& pre': {
                            backgroundColor: 'rgba(0, 0, 0, 0.05)',
                            padding: '0.75em',
                            borderRadius: '4px',
                            overflow: 'auto',
                            margin: '0.5em 0'
                          },
                          '& pre code': {
                            backgroundColor: 'transparent',
                            padding: 0
                          },
                          '& blockquote': {
                            borderLeft: '3px solid rgba(0, 0, 0, 0.2)',
                            paddingLeft: '1em',
                            margin: '0.5em 0',
                            fontStyle: 'italic'
                          },
                          '& a': {
                            color: '#1976d2',
                            textDecoration: 'underline'
                          },
                          // Make all direct children of this box have normal layout
                          '& > *': {
                            maxWidth: '100%',
                          }
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => (
                              <a
                                {...props}
                                target="_blank"
                                rel="noopener noreferrer"
                              />
                            ),
                            img: ({ node, ...props }) => (
                              <img
                                {...props}
                                style={{
                                  maxWidth: '100%',
                                  height: 'auto',
                                  display: 'block',
                                }}
                              />
                            ),
                            table: ({ node, ...props }) => (
                              <Box
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '400px',
                                  overflow: 'auto',
                                  margin: '0.5em 0',
                                  border: '1px solid rgba(0, 0, 0, 0.2)',
                                  borderRadius: '4px',
                                  backgroundColor: 'white',
                                }}
                              >
                                <table
                                  {...props}
                                  style={{
                                    borderCollapse: 'collapse',
                                    width: '100%',
                                    fontSize: '0.85em',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                                  }}
                                />
                              </Box>
                            ),
                            th: ({ node, ...props }) => (
                              <th
                                {...props}
                                style={{
                                  border: '1px solid rgba(0, 0, 0, 0.15)',
                                  padding: '8px 12px',
                                  textAlign: 'left',
                                  whiteSpace: 'nowrap',
                                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                  fontWeight: 600,
                                  position: 'sticky',
                                  top: 0,
                                  zIndex: 1,
                                }}
                              />
                            ),
                            td: ({ node, ...props }) => (
                              <td
                                {...props}
                                style={{
                                  border: '1px solid rgba(0, 0, 0, 0.15)',
                                  padding: '8px 12px',
                                  textAlign: 'left',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            ),
                            tbody: ({ node, ...props }) => (
                              <tbody
                                {...props}
                                style={{
                                  // Alternating row colors and hover effect
                                }}
                              />
                            ),
                            tr: ({ node, ...props }) => {
                              const isEven = props.children && Array.isArray(props.children)
                                ? (props.children.filter((c: any) => c?.type === 'td').length > 0)
                                : false;
                              return (
                                <tr
                                  {...props}
                                  style={{
                                    backgroundColor: isEven ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                                  }}
                                  onMouseEnter={(e) => {
                                    (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(245, 168, 0, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    (e.currentTarget as HTMLElement).style.backgroundColor = isEven ? 'rgba(0, 0, 0, 0.02)' : 'transparent';
                                  }}
                                />
                              );
                            },
                          }}
                        >
                          {msg.message}
                        </ReactMarkdown>
                      </Box>
                    </>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>

                    {/* Action buttons for assistant messages */}
                    {!msg.isUser && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        {/* Copy button */}
                        <IconButton
                          size="small"
                          onClick={() => handleCopyMessage(msg.id, msg.message)}
                          title="Copy to clipboard"
                          sx={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                          }}
                        >
                          {copiedMessageId === msg.id ? (
                            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <CopyIcon fontSize="small" />
                          )}
                        </IconButton>

                        {/* Thumbs up button */}
                        {messageFeedback[msg.id] !== 'down' && (
                          <IconButton
                            size="small"
                            onClick={() => handleThumbsUp(msg.id)}
                            title="Good response"
                            sx={{
                              color: messageFeedback[msg.id] === 'up' ? '#F5A800' : 'rgba(0, 0, 0, 0.6)',
                              backgroundColor: messageFeedback[msg.id] === 'up' ? 'rgba(245, 168, 0, 0.2)' : 'transparent',
                              '&:hover': { backgroundColor: 'rgba(245, 168, 0, 0.1)' },
                            }}
                          >
                            <ThumbUpIcon fontSize="small" />
                          </IconButton>
                        )}

                        {/* Thumbs down button */}
                        {messageFeedback[msg.id] !== 'up' && (
                          <IconButton
                            size="small"
                            onClick={() => handleThumbsDown(msg.id)}
                            title="Bad response"
                            sx={{
                              color: messageFeedback[msg.id] === 'down' ? '#d32f2f' : 'rgba(0, 0, 0, 0.6)',
                              backgroundColor: messageFeedback[msg.id] === 'down' ? 'rgba(211, 47, 47, 0.2)' : 'transparent',
                              '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.1)' },
                            }}
                          >
                            <ThumbDownIcon fontSize="small" />
                          </IconButton>
                        )}

                        {/* Email button */}
                        <IconButton
                          size="small"
                          onClick={handleEmailRequest}
                          title="Request summary email"
                          sx={{
                            color: 'rgba(0, 0, 0, 0.6)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' },
                          }}
                        >
                          <EmailIcon fontSize="small" />
                        </IconButton>

                        {/* Improve Mia button for admins */}
                        {hasAdminPermission && (
                          <Button
                            size="small"
                            startIcon={<ImproveIcon fontSize="small" />}
                            onClick={() => handleImproveMessage(msg.id)}
                            sx={{
                              fontSize: '0.7rem',
                              textTransform: 'none',
                              color: 'rgba(0, 0, 0, 0.6)',
                              minWidth: 'auto',
                              px: 1,
                              py: 0.25,
                              ml: 'auto',
                              '&:hover': {
                                backgroundColor: 'rgba(245, 168, 0, 0.1)',
                                color: '#F5A800',
                              },
                            }}
                          >
                            Improve Mia
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* Feedback form for thumbs down */}
                    {!msg.isUser && showFeedbackForm[msg.id] && (
                      <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          placeholder="Please tell us what was wrong with this response..."
                          value={feedbackText[msg.id] || ''}
                          onChange={(e) => setFeedbackText(prev => ({ ...prev, [msg.id]: e.target.value }))}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1 }}
                        />
                        <Button
                          onClick={() => handleSubmitFeedback(msg.id)}
                          disabled={!feedbackText[msg.id]?.trim()}
                          variant="contained"
                          size="small"
                          sx={{
                            backgroundColor: '#F5A800',
                            color: '#FFFFFF',
                            '&:hover': {
                              backgroundColor: '#D89500',
                            },
                            '&:disabled': {
                              backgroundColor: '#E0E0E0',
                              color: '#9E9E9E',
                            },
                          }}
                        >
                          Send Feedback
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Box>
            ))}

            {/* Intermediate messages from operation progress */}
            {intermediateMessages.map((intermediateMsg) => (
              <IntermediateMessage
                key={intermediateMsg.id}
                id={intermediateMsg.id}
                text={intermediateMsg.text}
                timestamp={intermediateMsg.timestamp}
                type={intermediateMsg.type as any}
                metadata={intermediateMsg.metadata}
              />
            ))}

            {/* Knight Rider loading animation while waiting for response */}
            {isSending && (
              <Box
                sx={{
                  px: 2,
                }}
              >
                <KnightRiderLoader />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    fontStyle: 'italic',
                    minHeight: '18px',
                  }}
                >
                  {operationProgress || 'Initializing...'}
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Input Box at Bottom */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1.5,
          backgroundColor: '#FFFFFF',
          borderTop: '1px solid #E0E0E0',
          // Add margin for BottomNav (80px)
          mb: '80px',
          // On mobile landscape, no BottomNav so no margin needed
          '@media (max-width: 768px) and (orientation: landscape)': {
            mb: 0,
          },
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={3}
          placeholder="Type your message..."
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
          variant="outlined"
          size="small"
        />
        <IconButton
          color="primary"
          onClick={() => handleSendMessage()}
          disabled={!inputMessage.trim() || isSending}
          sx={{
            backgroundColor: '#F5A800',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#D89500',
            },
            '&:disabled': {
              backgroundColor: '#E0E0E0',
              color: '#9E9E9E',
            },
          }}
        >
          {isSending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
        </IconButton>
      </Box>

      {/* Session History Drawer */}
      <Drawer
        anchor="right"
        open={showSessionDrawer}
        onClose={() => setShowSessionDrawer(false)}
      >
        <Box sx={{ width: 320, p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Chat History
            </Typography>
            <IconButton size="small" onClick={() => setShowSessionDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewChat}
            sx={{
              mb: 2,
              backgroundColor: '#F5A800',
              color: '#FFFFFF',
              '&:hover': {
                backgroundColor: '#D89500',
              },
            }}
          >
            New Chat
          </Button>

          <Divider sx={{ mb: 2 }} />

          {isLoadingSessions ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} sx={{ color: '#F5A800' }} />
            </Box>
          ) : sessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
              No previous chats
            </Typography>
          ) : (
            <List disablePadding>
              {sessions.map((session, index) => (
                <React.Fragment key={session.sessionId}>
                  {index > 0 && <Divider />}
                  <ListItem
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleDeleteSession(session.sessionId, e)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': {
                            color: 'error.main',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      onClick={() => handleLoadSession(session.sessionId)}
                      selected={session.sessionId === currentSessionId}
                      sx={{
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(245, 168, 0, 0.1)',
                          '&:hover': {
                            backgroundColor: 'rgba(245, 168, 0, 0.2)',
                          },
                        },
                        pr: 6, // Add padding to account for delete button
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              fontWeight: session.sessionId === currentSessionId ? 600 : 400,
                            }}
                          >
                            {session.lastMessage}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(session.lastMessageTime).toLocaleDateString([], {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                            {' • '}
                            {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>
      </Drawer>

      {/* Improve Message Modal */}
      {improveMessageId && (
        <ImproveMessageModal
          open={improveModalOpen}
          onClose={() => {
            setImproveModalOpen(false);
            setImproveMessageId(null);
          }}
          messageId={improveMessageId}
          messageContent={
            messages.find((m) => m.id === improveMessageId)?.message || ''
          }
        />
      )}
    </Box>
  );
};
